import type { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DrivelineSearchResult, useProductCenterApi } from 'api';
import Service from 'assets/volvoIcons/Misc/Service';
import { AreaName } from 'domain/constants';
import { getLinkCreateNewServiceProtocol } from 'library/components/link/linkUtils';
import { useAdobeTracking } from 'library/hooks/useAdobeTracking';
import { useDialogState } from 'library/hooks/useDialogState';
import { OperationType } from 'library/models/enums/OperationType';
import AuthorizeComponent from 'main/auth/AuthorizeComponent';

import { GenerateProtocolWarningDialog } from './GenerateProtocolWarningDialog';

interface ServiceButtonProps
	extends Pick<DrivelineSearchResult, 'chassisId' | 'id'> {
	ButtonElement: React.ElementType;
	buttonText?: string;
	serviceScheduledDate?: Date | null;
	buttonProps?: Omit<
		ButtonProps,
		'disabled' | 'onClick' | 'endIcon' | 'startIcon'
	>;
	openInNewWindow?: boolean;
}

export const ServiceButton = ({
	id,
	chassisId,
	serviceScheduledDate,
	ButtonElement,
	buttonText,
	buttonProps,
	openInNewWindow,
}: ServiceButtonProps): React.JSX.Element => {
	const { open, handleOpen, handleClose } = useDialogState();
	const [buttonDisabled, setButtonDisabled] = useState(false);
	const navigate = useNavigate();
	const { trackApplicationInteraction } = useAdobeTracking();

	const serviceProtocolId = useRef(0);
	const { api } = useProductCenterApi();

	const linkTarget = openInNewWindow ? '_blank' : '_self';

	const handleServiceButtonClicked = (
		event: React.MouseEvent<HTMLButtonElement>
	) => {
		event.stopPropagation();

		if (chassisId == null) {
			return;
		}

		void trackApplicationInteraction({
			action: 'click',
			stepName: 'MP Generate Protocol',
			selectedOptions: [{ attribute: 'chassisId', value: chassisId }],
		});

		event.currentTarget.disabled = true;
		setButtonDisabled(true);

		void (async () => {
			try {
				const response = await api.getLastInitiatedProtocolId({
					chassisId,
				});
				if (response.status == 200 && response.data > 0) {
					serviceProtocolId.current = response.data;
					handleOpen();
				} else {
					const link = getLinkCreateNewServiceProtocol(
						id,
						serviceScheduledDate,
						openInNewWindow
					);

					if (openInNewWindow) {
						window.open(link, linkTarget);
					} else {
						navigate(link);
					}
				}
			} finally {
				setButtonDisabled(false);
			}
		})();
	};

	return (
		<AuthorizeComponent
			areaName={AreaName.ServiceProtocol}
			permission={OperationType.Create}>
			<ButtonElement
				{...buttonProps}
				disabled={buttonDisabled}
				onClick={handleServiceButtonClicked}>
				{buttonDisabled ? (
					<CircularProgress size="1.5rem" />
				) : (
					<Service />
				)}
				{buttonText ? '\u00A0' + buttonText : ''}
			</ButtonElement>
			{open && (
				<GenerateProtocolWarningDialog
					drivelineId={id}
					serviceProtocolId={serviceProtocolId.current}
					serviceScheduledDate={serviceScheduledDate}
					chassisId={chassisId as string}
					onClose={handleClose}
					linkTarget={linkTarget}
				/>
			)}
		</AuthorizeComponent>
	);
};
